import { context } from '@opentelemetry/api'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
  SimpleSpanProcessor,
  SpanProcessor,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'

const SERVICE_NAME = import.meta.env.VITE_OTEL_SERVICE_NAME || 'hub-panel'
const OTEL_COLLECTOR_URL =
  import.meta.env.VITE_OTEL_EXPORTER_URL || 'https://otel.hubii.io/v1/traces'

const consoleExporter = new ConsoleSpanExporter()

const collectorExporter = new OTLPTraceExporter({
  url: OTEL_COLLECTOR_URL,
})

const spanProcessors: SpanProcessor[] = [new BatchSpanProcessor(collectorExporter)]

if (process.env.NODE_ENV === 'development') {
  spanProcessors.push(new SimpleSpanProcessor(consoleExporter))
}

const provider = new WebTracerProvider({
  resource: resourceFromAttributes({
    [ATTR_SERVICE_NAME]: SERVICE_NAME,
  }),
  spanProcessors,
})

const fetchInstrumentation = new FetchInstrumentation()

fetchInstrumentation.setTracerProvider(provider)

provider.register()

registerInstrumentations({
  instrumentations: [fetchInstrumentation],
  tracerProvider: provider,
})

const tracer = provider.getTracer(SERVICE_NAME)

console.log('OpenTelemetry initialized')

/**
 * Flattens a deeply nested object into a single-level object using dot notation for keys
 * This is necessary because OpenTelemetry only supports one level of attributes
 * @param obj The object to flatten
 * @param prefix Current key prefix (used for recursion)
 * @param delimiter The delimiter to use between nested keys (default: '.')
 * @returns A flattened object with dot notation keys
 */
export function flattenObject(
  obj: Record<string, any>,
  prefix: string = '',
  delimiter: string = '.',
): Record<string, any> {
  return Object.keys(obj).reduce((acc: Record<string, any>, key: string) => {
    const prefixedKey = prefix ? `${prefix}${delimiter}${key}` : key

    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      Object.assign(acc, flattenObject(obj[key], prefixedKey, delimiter))
    } else {
      acc[prefixedKey] = obj[key]
    }

    return acc
  }, {})
}

export function trackCustomEvent(eventName: string, attributes: Record<string, any> = {}) {
  const span = tracer.startSpan(eventName, undefined, context.active())

  span.setAttributes(flattenObject(attributes))
  span.end()
}
